import React, { useEffect, useMemo } from 'react';
import {
  IconButton, Box, Badge, BottomNavigation, BottomNavigationAction, Paper, useTheme, useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import { beforeCapture } from '../../utilities';
import HubSectionContainer from './CustomContainer';
import HubDrawer from './HubDrawer';
import { HubPetParentProvider, useHubPetParent } from './HubPetParentContext';
import { MessagesUnreadProvider, useMessagesUnread } from './Messages/MessagesUnreadContext';
import { getNavItems } from './hubNavItems';
import getHubAudience from './hubAudience';
import MoreSheet from './MoreSheet';
import ErrorPage from '../ErrorPage';
import hubTheme from './hubTheme';
import DevSettingsPanel from '../DevSettingsPanel';
// mixpanel.init lives in ./analytics so the public lesson pages, which render
// outside this tree, are covered too. Importing the module initialises it.
import './analytics';

function MixpanelIdentifier() {
  const { petParent } = useHubPetParent();
  useEffect(() => {
    if (petParent?.email) {
      mixpanel.identify(petParent.id);
      mixpanel.people.set({
        $name: `${petParent.firstName} ${petParent.lastName}`,
        $email: petParent.email,
      });
      mixpanel.register({ email: petParent.email });
    }
  }, [petParent?.email, petParent?.id, petParent?.firstName, petParent?.lastName]);
  return null;
}

// Height of the fixed mobile bottom bar. Shared with the body bottom padding below so
// page content is never hidden behind the bar.
const BOTTOM_NAV_HEIGHT = 64;

const bottomNavActionSx = {
  minWidth: 0,
  maxWidth: 'none',
  px: 0.5,
  py: 1,
  color: 'secondary.text',
  position: 'relative',
  '& > svg': { width: '1.5rem', height: '1.5rem' },
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.6875rem',
    lineHeight: 1.2,
    mt: 0.5,
    whiteSpace: 'nowrap',
    '&.Mui-selected': { fontSize: '0.6875rem' },
  },
  '&.Mui-selected': { color: 'primary.main' },
  // Small purple indicator at the top edge of the active tab.
  '&.Mui-selected::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 28,
    height: 3,
    borderRadius: '0 0 3px 3px',
    backgroundColor: 'primary.main',
  },
};

function HubBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { petParent } = useHubPetParent();
  const { unreadCount } = useMessagesUnread();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const membershipExpired = petParent?.membershipStatus === 'expired';
  const audience = getHubAudience(petParent);

  const { barItems, moreItems } = useMemo(() => {
    const items = getNavItems(membershipExpired, audience).filter((item) => item.visible);
    return {
      barItems: items.filter((item) => item.mobileSlot === 'bar'),
      moreItems: items.filter((item) => item.mobileSlot === 'more'),
    };
  }, [membershipExpired, audience]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // "More" occupies the slot after the bar items. When the active route lives in the More
  // sheet, the More entry shows the active state; otherwise the matching bar tab does.
  const moreIndex = barItems.length;
  const activeBarIndex = barItems.findIndex((item) => isActive(item.path));
  const activeInMore = moreItems.some((item) => isActive(item.path));
  let selectedValue: number | false = false;
  if (activeBarIndex >= 0) {
    selectedValue = activeBarIndex;
  } else if (activeInMore) {
    selectedValue = moreIndex;
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === moreIndex) {
      setMoreOpen(true);
      return;
    }
    navigate(barItems[newValue].path);
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1,
        }}
        elevation={3}
      >
        <BottomNavigation
          value={selectedValue}
          showLabels
          style={{ height: `${BOTTOM_NAV_HEIGHT}px` }}
          onChange={handleChange}
        >
          {barItems.map((item) => (
            <BottomNavigationAction
              key={item.name}
              label={item.barLabel || item.title}
              icon={item.name === 'hub/messages' ? (
                <Badge
                  badgeContent={unreadCount}
                  sx={{ '& .MuiBadge-badge': { bgcolor: '#d84315', color: '#fff' } }}
                >
                  <item.Icon />
                </Badge>
              ) : <item.Icon />}
              sx={bottomNavActionSx}
            />
          ))}
          {moreItems.length > 0 && (
            <BottomNavigationAction
              key="hub/more"
              label="More"
              value={moreIndex}
              icon={<MoreHorizIcon />}
              sx={bottomNavActionSx}
            />
          )}
        </BottomNavigation>
      </Paper>
      {moreItems.length > 0 && (
        <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} items={moreItems} />
      )}
    </>
  );
}

const FOOTER_BG_PATHS = ['/hub/home', '/hub/community', '/hub/my-pets', '/hub/settings'];
// Full-height pages (e.g. the chat-style Messages thread) own their own viewport
// and should not render the shared Hub logo footer below the fold.
const NO_FOOTER_PATHS = ['/hub/messages'];

function HubContainer() {
  console.log(process.env.REACT_APP_CONTEXT);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
    noSsr: true,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  return (
    <ThemeProvider theme={hubTheme}>
      <HubPetParentProvider>
        <MessagesUnreadProvider>
          <MixpanelIdentifier />
          <Box sx={{
            // Use dvh (the *visible* viewport) rather than vh: on mobile, 100vh
            // extends behind the browser URL bar, making the shell taller than the
            // screen and producing a stray outer scroll on top of the Messages
            // thread's own internal scroll. dvh tracks the visible area so short
            // pages fill exactly the screen and tall pages still scroll the window.
            minHeight: '100dvh', display: 'flex', flexDirection: 'column', pb: isMobile ? `${BOTTOM_NAV_HEIGHT}px` : 0,
          }}
          >
            <HubDrawer
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              toggleDrawer={toggleDrawer}
              isDesktop={isDesktop}
            />
            {
            isMobile && (
              <Box
                sx={{
                  flexShrink: 0,
                  bgcolor: '#fff',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  zIndex: 2,
                }}
              >
                <HubSectionContainer>
                  <Box sx={{
                    py: 1, mx: -1, textAlign: 'center',
                  }}
                  >
                    <IconButton
                      sx={{
                        borderRadius: '6px',
                        m: 0,
                      }}
                      edge="start"
                      color="inherit"
                      onClick={() => navigate('/hub/home')}
                    >
                      <img style={{}} alt="Petcademy Logo" src="/logo_hub.svg" />
                    </IconButton>
                  </Box>
                </HubSectionContainer>
              </Box>
            )
          }
            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              paddingLeft: { xs: 0, md: 'calc(3.5rem + 1px)' },
            }}
            >
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
              >
                <Sentry.ErrorBoundary
                  key={location.pathname}
                  beforeCapture={beforeCapture}
                  fallback={<ErrorPage embedded />}
                >
                  <Outlet />
                </Sentry.ErrorBoundary>
              </Box>
            </Box>
            {!NO_FOOTER_PATHS.includes(location.pathname) && (
              <Box
                component="footer"
                sx={{
                  pt: 6,
                  pb: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  background: FOOTER_BG_PATHS.includes(location.pathname) ? '#F6F5FA' : '',
                  pl: {
                    xs: 0,
                    md: 'calc(3.5rem + 1px)',
                  },
                }}
              >
                <img style={{ opacity: 0.55, filter: 'grayscale(100%)' }} alt="Petcademy Logo Footer" src="/logo_hub.svg" />
              </Box>
            )}
            {isMobile && <HubBottomNav />}
            {
            process.env.REACT_APP_CONTEXT !== 'production' && false && (
              <Box sx={{
                position: 'fixed', left: '0px', bottom: '0px', zIndex: 1300,
              }}
              >
                <DevSettingsPanel />
              </Box>
            )
          }
          </Box>
        </MessagesUnreadProvider>
      </HubPetParentProvider>
    </ThemeProvider>
  );
}

export default HubContainer;
